import { z } from 'zod';
import {
  DisplayrProviderDataSchema,
  PageTypeEnum,
  SigmaProviderDataSchema,
  SigmaTabEnum,
  TableauProviderDataSchema,
} from '@trinity/management-app-client';

// Pages attached to a project or brand and shown as its tabs (chat-api's project_pages /
// brand_pages). The page holds the tab name (`name`) and its settings (its current version's
// config); the link holds the tab order and publish state.

export const EntityPageOwnerTypeEnum = z.enum(['project', 'brand']);
export type EntityPageOwnerType = z.infer<typeof EntityPageOwnerTypeEnum>;

/** The project or brand a page is attached to. */
export type EntityPageOwner = { type: EntityPageOwnerType; id: number };

/**
 * The embeds a project / brand dashboard can be created as: the providers apps/frontend's
 * external dashboards supported. Other page types can be attached later without a schema change.
 */
export const EntityDashboardProviderDataSchema = z.discriminatedUnion('provider', [
  DisplayrProviderDataSchema,
  SigmaProviderDataSchema,
  TableauProviderDataSchema,
]);
export type EntityDashboardProviderData = z.infer<typeof EntityDashboardProviderDataSchema>;
export type EntityDashboardProvider = EntityDashboardProviderData['provider'];

const PageNameSchema = z
  .string()
  .trim()
  .min(1)
  .max(255)
  .refine((value) => !/[<>]/.test(value), `'<' and '>' are not allowed`);

export const EntityPageSchema = z.object({
  identifier: z.uuid(),
  name: z.string(),
  type: PageTypeEnum,
  /** The page's current config: provider settings for a third_party page, a DashboardConfig otherwise. */
  config: z.record(z.string(), z.unknown()).nullable(),
  sortOrder: z.number().int(),
  publishedOn: z.coerce.date().nullable(),
  updatedOn: z.coerce.date(),
});
export type EntityPage = z.infer<typeof EntityPageSchema>;

export const EntityPageIdentifierParamsSchema = z.object({ pageIdentifier: z.uuid() });

// POST /<owner>/pages
export const CreateEntityDashboardBodySchema = z.object({
  name: PageNameSchema,
  providerData: EntityDashboardProviderDataSchema,
});
export type CreateEntityDashboardBody = z.infer<typeof CreateEntityDashboardBodySchema>;

// PATCH /<owner>/pages/:pageIdentifier — every field optional, at least one present.
export const UpdateEntityDashboardBodySchema = z
  .object({
    name: PageNameSchema.optional(),
    providerData: EntityDashboardProviderDataSchema.optional(),
    sortOrder: z.number().int().min(0).max(10_000).optional(),
  })
  .refine(
    (body) => body.name !== undefined || body.providerData !== undefined || body.sortOrder !== undefined,
    'Nothing to update'
  );
export type UpdateEntityDashboardBody = z.infer<typeof UpdateEntityDashboardBodySchema>;

export const EntityPageResponseSchema = z.object({ page: EntityPageSchema });
export type EntityPageResponse = z.infer<typeof EntityPageResponseSchema>;

// GET /<owner>/pages/:pageIdentifier/sigma-url?tab=
export const EntityPageSigmaUrlQuerySchema = z.object({ tab: SigmaTabEnum });
export const EntityPageSigmaUrlResponseSchema = z.object({ url: z.url() });
